import { toDate } from './orderCardFormat';

// Bygger tidslinjen for et oppdrag ut fra det som faktisk er lagret: ordren, budene, fakturaen og
// kreditnotaer. Bare hendelser med belegg i dataene tas med. En hendelse som vi vet har skjedd, men
// som ikke har lagret tidspunkt (f.eks. «Sjåfør tildelt» på eldre oppdrag), vises uten klokkeslett
// i stedet for med et oppdiktet.

const num = (n) => Number(n).toLocaleString('nb-NO');
const kr = (n) => `kr ${num(n)}`;
const signedKr = (n) => `${n > 0 ? '+' : '−'} ${kr(Math.abs(n))}`;
const dayMonth = (d) => d.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });

const OUTCOME_TEXT = {
  refund_orderer: () => 'Tippi avgjorde at bestiller får pengene tilbake.',
  pay_driver: () => 'Tippi avgjorde at sjåføren får full betaling.',
  split: (res, order) => {
    const pct = Number(res.driverPct);
    const base = `Tippi delte beløpet: sjåføren får ${num(pct)} %, bestiller ${num(100 - pct)} %`;
    return order.settledAmount != null ? `${base} (sjåførens andel ${kr(order.settledAmount)}).` : `${base}.`;
  },
};

// Rekkefølgen brukes når tidspunkt mangler eller er like
const RANK = [
  'created', 'bids', 'accepted', 'assigned', 'pickup', 'adjustment', 'adjustmentApproved', 'transport',
  'delivered', 'lateAdd', 'receipts', 'disputed', 'disputeResolved', 'escrowReleased', 'confirmed', 'completed',
  'invoice', 'overdue', 'paid', 'credit', 'cancelled', 'refunded',
];
const rankOf = (key) => {
  const i = RANK.indexOf(key.split(':')[0]);
  return i === -1 ? RANK.length : i;
};

const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

function isTakeBid(bid) {
  return bid?.source === 'take';
}

export function buildOrderTimeline({ order, bids = [], invoice = null, creditNotes = [], role = 'orderer' } = {}) {
  if (!order) return [];
  const driver = role === 'driver';
  const unit = order.unit === 'bags' ? 'sekker' : 'tonn';
  const status = order.status;
  const events = [];
  const add = (key, title, at, extra = {}) => {
    const e = { key, title, at: toDate(at), ...extra };
    if (!e.detail) delete e.detail;
    if (!e.media?.length) delete e.media;
    if (!e.tone) delete e.tone;
    events.push(e);
  };

  // ── Opprettet ──
  const fixed = Number.isInteger(order.fixedPrice) && order.fixedPrice > 0;
  const mass = order.estimatedTons != null ? `${num(order.estimatedTons)} ${unit}` : null;
  const priceText = fixed
    ? `Fastpris ${kr(order.fixedPrice)}${order.autoAccept ? ' · første godkjente sjåfør får oppdraget' : ''}`
    : 'Anbud';
  add('created', 'Oppdrag opprettet', order.createdAt, { detail: [priceText, mass].filter(Boolean).join(' · ') });

  // ── Bud ──
  const acceptedBid = bids.find((b) => b.id === order.acceptedBidId) ?? null;
  const offered = bids.filter((b) => !isTakeBid(b));
  if (offered.length > 0) {
    const times = offered.map((b) => toDate(b.createdAt)).filter(Boolean).sort((a, b) => a - b);
    const lowest = Math.min(...offered.map((b) => Number(b.amount)).filter(Number.isFinite));
    if (driver) {
      add('bids', 'Du la inn bud', times[0] ?? null, { detail: `Ditt bud: ${kr(offered[0].amount)}` });
    } else {
      const detail = `${offered.length} bud${Number.isFinite(lowest) ? ` · laveste ${kr(lowest)}` : ''}`;
      add('bids', 'Bud mottatt', times[0] ?? null, { detail });
    }
  }

  // ── Godtatt og tildelt ──
  const matched = !!(order.acceptedBidId || order.matchedAt || order.driverUid || order.agreedPrice != null);
  if (matched) {
    const price = order.agreedPrice ?? acceptedBid?.amount;
    const priceDetail = price != null ? kr(price) : null;
    if (isTakeBid(acceptedBid)) {
      add('accepted', driver ? 'Du godtok fastprisen' : 'Fastpris godtatt', order.matchedAt, { detail: priceDetail });
    } else if (acceptedBid) {
      add('accepted', driver ? 'Budet ditt ble godtatt' : 'Bud godtatt', order.matchedAt, { detail: priceDetail });
    } else {
      add('accepted', 'Pris godtatt', order.matchedAt, { detail: priceDetail });
    }
    const name = acceptedBid?.driverName;
    add('assigned', driver ? 'Oppdraget ble tildelt deg' : 'Sjåfør tildelt', order.matchedAt, {
      detail: driver ? null : (name || null),
    });
  }

  // ── Henting ──
  const pickupPhotos = list(order.pickupPhotoUrls);
  const pickedUp = !!(order.pickupRegisteredAt || order.pickupActualTons != null || pickupPhotos.length || order.weightDocumentUrl);
  if (pickedUp) {
    const media = [
      ...pickupPhotos.map((url, i) => ({ type: 'photo', url, label: `Hentebilde ${i + 1}` })),
      ...(order.weightDocumentUrl ? [{ type: 'document', url: order.weightDocumentUrl, label: 'Veieseddel' }] : []),
    ];
    let detail = null;
    if (order.pickupActualTons != null) {
      detail = `${num(order.pickupActualTons)} ${unit} veid`;
      if (order.estimatedTons != null) detail += ` (bestilt ${num(order.estimatedTons)})`;
    }
    add('pickup', 'Lasset hentet', order.pickupRegisteredAt, { detail, media });

    const adj = order.pendingAdjustment;
    if (adj) {
      const parts = [];
      if (adj.actualTons != null && adj.estimatedTons != null) {
        parts.push(`Veid ${num(adj.actualTons)} ${unit} mot estimert ${num(adj.estimatedTons)}`);
      }
      if (adj.deviationPct != null) parts.push(`avvik ${num(adj.deviationPct)} %`);
      if (adj.priceAdjustment != null) parts.push(`prisjustering ${signedKr(adj.priceAdjustment)}`);
      add('adjustment', 'Vektavvik registrert', order.pickupRegisteredAt, { detail: parts.join(' · '), tone: 'warning' });
      if (adj.approvedByOrderer) {
        add('adjustmentApproved', driver ? 'Bestiller godkjente vektavviket' : 'Vektavvik godkjent', null, { tone: 'success' });
      }
    }
    add('transport', 'Transport startet', order.pickupRegisteredAt);
  }

  // ── Levering ──
  const deliveryPhotos = list(order.deliveryPhotoUrls);
  const signature = order.receiverSignatureUrl;
  if (order.deliveredAt || deliveryPhotos.length || signature) {
    const media = [
      ...deliveryPhotos.map((url, i) => ({ type: 'photo', url, label: `Leveringsbilde ${i + 1}` })),
      ...(signature ? [{ type: 'signature', url: signature, label: 'Signatur fra mottaker' }] : []),
    ];
    const parts = [];
    if (order.pickupActualTons != null) parts.push(`${num(order.pickupActualTons)} ${unit} levert`);
    if (signature) parts.push('Signert av mottaker');
    add('delivered', signature ? 'Levert og signert' : 'Levert', order.deliveredAt, { detail: parts.join(' · '), media });
  }

  // ── Bilder lagt til i etterkant (sjåføren, før bestiller har bekreftet) ──
  list(order.lateAdditions).forEach((add2, i) => {
    const urls = list(add2.urls);
    if (!urls.length) return;
    const after = add2.stage === 'delivery' ? 'levering' : 'henting';
    add(`lateAdd:${i}`, driver ? `Du la til bilder etter ${after}` : `Sjåføren la til bilder etter ${after}`, add2.addedAt, {
      detail: 'Lagt til i etterkant.',
      media: urls.map((url, j) => ({ type: 'photo', url, label: `${add2.stage === 'delivery' ? 'Leveringsbilde' : 'Hentebilde'} lagt til senere ${j + 1}` })),
    });
  });

  // ── Billag (veiesedler og kvitteringer fra sjåføren), i opplastingsrekkefølge ──
  // Elementene har ikke eget tidspunkt, så gruppen legges etter leveringen og bildene i etterkant.
  const receipts = list(order.receipts).filter((r) => r.url);
  if (receipts.length) {
    const notes = receipts.map((r, i) => (r.note ? `Billag ${i + 1}: ${r.note}` : null)).filter(Boolean);
    add('receipts', driver ? 'Du lastet opp billag' : 'Billag fra sjåføren', null, {
      detail: notes.join(' · '),
      media: receipts.map((r, i) => ({ type: 'photo', url: r.url, label: `Billag ${i + 1}` })),
    });
  }

  // ── Tvist ──
  const resolution = order.disputeResolution;
  if (status === 'disputed' || resolution || order.disputeVerdict) {
    add('disputed', 'Levering bestridt', null, {
      detail: driver ? 'Bestiller bestred leveringen.' : 'Du bestred leveringen.',
      tone: 'danger',
    });
  }
  if (resolution) {
    const text = OUTCOME_TEXT[resolution.outcome]?.(resolution, order) ?? null;
    add('disputeResolved', 'Tvist løst', order.disputeResolvedAt, { detail: text, tone: 'success' });
  }

  // ── Bekreftelse og fullføring ──
  const done = status === 'completed' || status === 'invoiced';
  const released = order.escrowReleasedByAdmin === true;
  const completionAt = order.confirmedAt ?? order.escrowReleasedAt ?? order.disputeResolvedAt ?? null;
  if (released) {
    add('escrowReleased', 'Utbetaling frigitt av Tippi', order.escrowReleasedAt, { tone: 'success' });
  } else if ((done || order.confirmedAt) && !resolution && order.deliveredAt) {
    add('confirmed', 'Levering bekreftet', order.confirmedAt, {
      detail: order.confirmedBy === 'auto' ? 'Automatisk bekreftet etter 24 timer uten svar.' : null,
      tone: 'success',
    });
  }
  if (done) add('completed', 'Oppdrag fullført', completionAt, { tone: 'success' });

  // ── Faktura, betaling og kreditnotaer ──
  if (invoice) {
    const direct = invoice.kind === 'driver-to-orderer' || invoice.invoiceNumber != null;
    const total = direct ? invoice.grossAmount : invoice.totalAmount;
    const issuedAt = direct ? invoice.issueDate : invoice.createdAt;
    const due = direct ? toDate(invoice.dueDate) : null;
    const detail = [
      direct && invoice.invoiceNumber != null ? `Faktura ${invoice.invoiceNumber}` : null,
      total != null ? kr(total) : null,
      due ? `forfall ${dayMonth(due)}` : null,
    ].filter(Boolean).join(' · ');
    add('invoice', 'Faktura utstedt', issuedAt, { detail });

    if (direct && invoice.status === 'overdue') {
      add('overdue', 'Faktura forfalt', invoice.overdueAt, { tone: 'warning' });
    }
    if (direct ? invoice.status === 'paid' : invoice.status === 'captured') {
      add('paid', 'Betalt', direct ? invoice.paidMarkedAt : invoice.paidAt, {
        detail: direct ? 'Markert som betalt' : (total != null ? kr(total) : null),
        tone: 'success',
      });
    }
  }
  for (const note of creditNotes) {
    add(`credit:${note.id ?? note.invoiceNumber}`, `Kreditnota${note.invoiceNumber != null ? ` ${note.invoiceNumber}` : ''}`,
      note.issueDate ?? note.createdAt, {
        detail: [note.creditsInvoiceNumber != null ? `Krediterer faktura ${note.creditsInvoiceNumber}` : null,
          note.grossAmount != null ? kr(Math.abs(note.grossAmount)) : null].filter(Boolean).join(' · '),
        tone: 'warning',
      });
  }

  // ── Kansellert / refundert ──
  if (status === 'cancelled') {
    if (order.cancelledByAdminAt) add('cancelled', 'Kansellert av Tippi', order.cancelledByAdminAt, { tone: 'danger' });
    else if (order.cancelledByOrdererAt) add('cancelled', driver ? 'Kansellert av bestiller' : 'Kansellert av deg', order.cancelledByOrdererAt, { tone: 'danger' });
    else add('cancelled', 'Kansellert', null, { tone: 'danger' });
  }
  if (status === 'refunded' && !resolution) {
    add('refunded', 'Refundert', order.refundedAt, {
      detail: driver ? null : 'Beløpet er tilbakebetalt til bestiller.',
      tone: 'warning',
    });
  }

  return sortEvents(events);
}

// Sorterer på tidspunkt. En hendelse uten tidspunkt arver tidspunktet til hendelsen foran seg i
// den faste rekkefølgen, så den blir stående på riktig sted. Lik tid avgjøres av den faste rekkefølgen.
function sortEvents(events) {
  const byRank = events.map((e, i) => ({ e, i, rank: rankOf(e.key) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i);
  let carried = -Infinity;
  for (const item of byRank) {
    if (item.e.at) carried = Math.max(carried, item.e.at.getTime());
    item.eff = item.e.at ? item.e.at.getTime() : carried;
  }
  return byRank
    .sort((a, b) => a.eff - b.eff || a.rank - b.rank || a.i - b.i)
    .map((x) => x.e);
}

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const hhmm = (d) => d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });

// «I DAG 14:20», «I GÅR 14:20», ellers «3. okt · 09:21» (med år når det er et annet år)
export function formatEventTime(date, now = new Date()) {
  if (!date) return null;
  const diff = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diff === 0) return `I DAG ${hhmm(date)}`;
  if (diff === 1) return `I GÅR ${hhmm(date)}`;
  const opts = date.getFullYear() === now.getFullYear()
    ? { day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' };
  return `${date.toLocaleDateString('nb-NO', opts)} · ${hhmm(date)}`;
}

export const FINAL_ORDER_STATUSES = ['completed', 'invoiced', 'refunded', 'cancelled', 'disputed'];
